import { type SiteVideo, clock, embedUrl, watchUrl } from "../data/videos";

/* A YouTube video with its chapters listed under it. The iframe is a plain
   lazy embed rather than a click-to-load facade: search has to see a real
   video on the page for the video markup to count, and loading="lazy" keeps
   it from costing anything until it is scrolled near. The chapter links are
   the same moments the markup lists, so a reader can jump straight in. */
export default function VideoEmbed({ video, id = "video" }: { video: SiteVideo; id?: string }) {
  return (
    <figure id={id} className="not-prose my-10 scroll-mt-24">
      <div className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black">
        <iframe
          src={embedUrl(video)}
          title={video.title}
          loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
        />
      </div>
      <figcaption className="mt-3 text-sm text-white/55">
        <span className="text-white/75">{video.description}</span>{" "}
        <a href={watchUrl(video)} target="_blank" rel="noopener noreferrer" className="text-primary-light hover:text-white">
          Watch on YouTube ({clock(video.seconds)})
        </a>
        <span className="mt-3 flex flex-wrap gap-2" role="list" aria-label="Chapters">
          {video.chapters.map((c) => (
            <span key={c.at} role="listitem">
              <a
                href={watchUrl(video, c.at)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-2.5 py-1 text-xs text-white/65 transition-colors hover:border-primary/40 hover:text-white"
              >
                <span className="tabular-nums text-white/40">{clock(c.at)}</span>
                {c.label}
              </a>
            </span>
          ))}
        </span>
      </figcaption>
    </figure>
  );
}
